import { useEffect, useState } from 'react';
import { stopMediaStream } from '../utils/media';

/**
 * Calculates microphone input level for the selected device.
 */
export function useAudioLevel(deviceId: string): number {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    let cleanup: () => void = () => undefined;

    const setup = async () => {
      cleanup = await startAudioMeter(deviceId, setLevel);
    };

    void setup();

    return () => cleanup();
  }, [deviceId]);

  return level;
}

/**
 * Starts the audio meter and returns a cleanup function.
 */
async function startAudioMeter(
  deviceId: string,
  onLevel: (level: number) => void
): Promise<() => void> {
  try {
    const stream = await getAudioStream(deviceId);
    const { audioContext, analyser } = createAnalyser(stream);
    const loop = startLevelLoop(analyser, onLevel);

    return () => {
      loop.stop();
      void audioContext.close();
      stopMediaStream(stream);
    };
  } catch {
    onLevel(0);
    return () => undefined;
  }
}

/**
 * Requests a microphone stream for the device.
 */
async function getAudioStream(deviceId: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: deviceId ? { deviceId: { exact: deviceId } } : true,
    video: false,
  });
}

/**
 * Creates an analyser node for audio levels.
 */
function createAnalyser(stream: MediaStream): { audioContext: AudioContext; analyser: AnalyserNode } {
  const audioContext = new AudioContext();
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 512;
  audioContext.createMediaStreamSource(stream).connect(analyser);
  return { audioContext, analyser };
}

/**
 * Starts an animation loop to sample audio levels.
 */
function startLevelLoop(
  analyser: AnalyserNode,
  onLevel: (level: number) => void
): { stop: () => void } {
  const data = new Uint8Array(analyser.frequencyBinCount);
  let frameId = 0;

  const update = () => {
    analyser.getByteTimeDomainData(data);
    onLevel(calculateRms(data));
    frameId = requestAnimationFrame(update);
  };

  update();

  return {
    stop: () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    },
  };
}

/**
 * Calculates RMS level from time-domain audio data.
 */
function calculateRms(data: Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < data.length; i += 1) {
    const normalized = (data[i] - 128) / 128;
    sum += normalized * normalized;
  }

  return Math.sqrt(sum / data.length);
}
